import type { ThreadId } from '../types';

// A sync reset drops every chat's cached history, and a WebSocket reconnect triggers one whenever the
// network hiccups — so stepping back into a chat had to refetch it from scratch, showing only its last
// message with a blank gap above it. The threads the user actually moves between keep their on-screen
// slice across that reset; the refresh below then merges over it, so state stays server-authoritative.
//
// Deliberately module state rather than `GlobalState`: it is a hint for the current session only, so it
// needs no cache migration, and a reload — which refetches everything anyway — starts it empty.
const MAX_RECENT_THREADS = 10;

type RecentThread = {
  chatId: string;
  threadId: ThreadId;
};

const recentThreads: RecentThread[] = [];
const staleThreadKeys = new Set<string>();

function buildThreadKey(chatId: string, threadId: ThreadId) {
  return `${chatId}_${threadId}`;
}

export function markThreadViewed(chatId: string, threadId: ThreadId) {
  const index = recentThreads.findIndex((thread) => thread.chatId === chatId && thread.threadId === threadId);
  if (index !== -1) {
    recentThreads.splice(index, 1);
  }

  recentThreads.unshift({ chatId, threadId });
  recentThreads.length = Math.min(recentThreads.length, MAX_RECENT_THREADS);
}

export function getRecentlyViewedThreads(): readonly RecentThread[] {
  return recentThreads;
}

// History that survived a sync reset renders at once, but the server has not confirmed it since the
// connection dropped. Marking it forces exactly one refresh when the thread is next opened, so a
// message deleted while the socket was down cannot linger.
export function markThreadsAwaitingRefresh(threads: readonly RecentThread[]) {
  threads.forEach(({ chatId, threadId }) => staleThreadKeys.add(buildThreadKey(chatId, threadId)));
}

export function consumeThreadRefresh(chatId: string, threadId: ThreadId) {
  const key = buildThreadKey(chatId, threadId);
  if (!staleThreadKeys.has(key)) return false;

  staleThreadKeys.delete(key);

  return true;
}
